import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ScrollRevealProvider } from "@/contexts/ScrollRevealContext";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";

// Lazy load all routes except Index (landing page should load immediately)
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const BrandDetails = lazy(() => import("./pages/BrandDetails"));
const BrandMascot = lazy(() => import("./pages/BrandMascot"));
const Result = lazy(() => import("./pages/Result"));
const ApprovedBrand = lazy(() => import("./pages/ApprovedBrand"));
const FastBranding = lazy(() => import("./pages/FastBranding"));
const MascoteGenerator = lazy(() => import("./pages/MascoteGenerator"));
const TestLogoGeneration = lazy(() => import("./pages/TestLogoGeneration"));
const Checkout = lazy(() => import("./pages/Checkout"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const Admin = lazy(() => import("./pages/Admin"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ScrollRevealProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/brand/:brandId" element={<BrandDetails />} />
                <Route path="/brand/:brandId/mascot" element={<BrandMascot />} />
                <Route path="/result" element={<Result />} />
                <Route path="/approved-brand" element={<ApprovedBrand />} />
                <Route path="/criar-marca" element={<FastBranding />} />
                <Route path="/mascote-generator" element={<MascoteGenerator />} />
                <Route path="/test-logo-generation" element={<TestLogoGeneration />} />
                <Route path="/termos" element={<TermosDeUso />} />
                <Route path="/terms" element={<TermosDeUso />} />
                <Route path="/privacidade" element={<PoliticaPrivacidade />} />
                <Route path="/privacy" element={<PoliticaPrivacidade />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ScrollRevealProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
